/**
 * API клієнт для doc.cash_shift_close (Порція Роздріб.4/5a).
 * Backend: /api/v1/retail/cash-shift-close. Permissions: retail.shift.close.
 */
import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

export type ShortageHandling = 'none' | 'forgive' | 'recover_from_cashier';
export type CashCollectionMethod =
  | 'none'
  | 'bank_deposit'
  | 'collector'
  | 'transferred_to_next_shift';
export type FiscalZStatus = 'not_fiscalized' | 'pending' | 'fiscalized' | 'failed' | 'demo';

export interface CashShiftClose {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  shift_open_id: string;
  pos_workstation_id: string;
  cashier_user_id: string;

  total_sales_count: number;
  total_sales_amount: string;
  total_cash_received: string;
  total_card_received: string;
  total_bonus_writeoff: string;

  total_returns_count: number;
  total_returns_amount: string;
  cash_returns: string;
  card_returns: string;
  bonus_returns: string;

  total_bonus_accrued: string;

  opening_cash: string;
  expected_cash: string;
  actual_cash: string;
  cash_difference: string | null;

  shortage_handling: ShortageHandling | null;
  cash_collected: string;
  cash_remaining: string;
  cash_collection_method: CashCollectionMethod | null;

  fiscal_z_report_number: string | null;
  fiscal_z_report_url: string | null;
  fiscal_z_status: FiscalZStatus;
  fiscalized_at: string | null;

  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  created_at: string;
  created_by: string | null;
  comment: string | null;
  // Block 5.3f:
  state?: CancellableState;
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface CashShiftCloseCreate {
  organization_id: string;
  document_number?: string | null;
  document_date?: string | null;
  shift_open_id: string;
  actual_cash: string;
  shortage_handling?: ShortageHandling;
  cash_collected?: string;
  cash_collection_method?: CashCollectionMethod;
  comment?: string | null;
}

export type CashShiftCloseUpdate = Partial<Omit<CashShiftCloseCreate, 'organization_id' | 'shift_open_id'>>;

export interface CashShiftCloseFiscalizeOut {
  document: CashShiftClose;
  fiscal_z_status: FiscalZStatus;
  fiscal_z_report_number: string | null;
  fiscal_z_report_url: string | null;
  fiscalized_at: string | null;
  error_message?: string | null;
}

export const retailCashShiftCloses = {
  list: (params?: {
    organization_id?: string;
    shift_open_id?: string;
    posted?: boolean;
    fiscal_z_status?: FiscalZStatus;
    limit?: number;
    offset?: number;
  }) =>
    api.get<Page<CashShiftClose>>('/retail/cash-shift-close', { params }).then((r) => r.data),
  get: (id: string) => api.get<CashShiftClose>(`/retail/cash-shift-close/${id}`).then((r) => r.data),
  create: (payload: CashShiftCloseCreate) =>
    api.post<CashShiftClose>('/retail/cash-shift-close', payload).then((r) => r.data),
  update: (id: string, payload: CashShiftCloseUpdate) =>
    api.patch<CashShiftClose>(`/retail/cash-shift-close/${id}`, payload).then((r) => r.data),
  fiscalizeZReport: (id: string) =>
    api.post<CashShiftCloseFiscalizeOut>(`/retail/cash-shift-close/${id}/fiscalize-z-report`)
      .then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/retail/cash-shift-close/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};
